import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';
import BrainIcon from '@mui/icons-material/Psychology';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import ApiIcon from '@mui/icons-material/Api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import HistoryIcon from '@mui/icons-material/History';
import { Avatar } from '@mui/material';
import { CrawlProvider } from '../../context/CrawlContext';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const menuItems = [
    { text: '크롤링 관리', icon: <LanguageIcon />, path: '/admin/crawl' },
    // { text: '문서 관리', icon: <InsertDriveFileIcon />, path: '/admin/documents' },  // 임시 비활성화
    // { text: 'DB 연결 관리', icon: <StorageIcon />, path: '/admin/database' },  // 임시 비활성화
    { text: '지식베이스 구축', icon: <BrainIcon />, path: '/admin/knowledge-base' },
    { text: '큐레이션된 답변 관리', icon: <QuestionAnswerIcon />, path: '/admin/faq' },
    { text: '질의응답 내역', icon: <HistoryIcon />, path: '/admin/chat-history' },
    // { text: '모델 관리', icon: <ModelTrainingIcon />, path: '/admin/models' },  // 임시 비활성화
    // { text: 'API 도구 관리', icon: <ApiIcon />, path: '/admin/api-tools' },  // 임시 비활성화
    { text: '관리자 계정 관리', icon: <AdminPanelSettingsIcon />, path: '/admin/admin-users' },
  ];

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    // 로컬 스토리지에서 토큰 제거
    localStorage.removeItem('adminToken');
    
    // 다이얼로그 닫기
    setLogoutDialogOpen(false);
    
    // 메인 페이지로 이동
    navigate('/');
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const drawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="div">
          관리자 메뉴
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/')}>
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="사용자 페이지로 이동" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="로그아웃" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="secondary">
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <AdminPanelSettingsIcon />
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              빅스데이터 AI 기반 질의응답 시스템 관리자
            </Typography>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: 'white',
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                    borderBottom: isActive(item.path) ? '2px solid white' : 'none',
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 1,
                    mx: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                onClick={() => navigate('/')}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 1,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                사용자 페이지
              </Button>
              <Button
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 1,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 99, 71, 0.2)',
                    borderColor: 'rgba(255, 99, 71, 0.4)',
                  },
                }}
              >
                로그아웃
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawer}
      </Drawer>
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 3 }}>
        <CrawlProvider>
          <Outlet />
        </CrawlProvider>
      </Container>
      
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) => theme.palette.grey[200],
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} 빅스데이터 AI 기반 질의응답 시스템
          </Typography>
        </Container>
      </Box>

      {/* 로그아웃 확인 다이얼로그 */}
      <Dialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>로그아웃 확인</DialogTitle>
        <DialogContent>
          <Typography>
            정말로 로그아웃하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            로그아웃 후 관리자 기능을 사용하려면 다시 로그인해야 합니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLogout}>취소</Button>
          <Button onClick={confirmLogout} variant="contained" color="error">
            로그아웃
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLayout; 